import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { useToast } from "@chakra-ui/react";
import {
    User as Entity,
    UserApi as EntityApi,
    Internship,
    InternshipSchedule,
    Partnership,
    PartnershipApi,
    School,
} from "../../../apis";
import { AppFormContainer } from "../../../containers";
import { AppLoader } from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC } from "../../../config";
import { InternshipScheduleStudentDetailView } from "./InternshipScheduleStudentDetailView";

export const InternshipScheduleStudentForm: FC<{
    formContainerState: FormContainerStateReturnType;
    isDetailPage: boolean;
    internshipSchedule: InternshipSchedule | null;
}> = ({ formContainerState, isDetailPage, internshipSchedule }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<Entity>(new Entity());
    const [internshipCoordinatorTrainer, setInternshipCoordinatorTrainer] =
        useState<Entity[]>([]);
    const [internshipReferenceTrainer, setInternshipReferenceTrainer] =
        useState<Entity | undefined>();
    const USERTYPE = APPC.BE.User.USERTYPE;

    // useEffect
    useEffect(() => {
        if (!formContainerState.entityId) {
            setData(new Entity());
            setLoading(false);
            return;
        }
        setLoading(true);
        EntityApi.getItemStudent<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    setData(response);
                    const schoolId = (response.schools?.[0] as School)?.["@id"];
                    if (schoolId) {
                        const coordinatorParams = {
                            [`order[name]`]: "asc",
                            pagination: false,
                            userType: [
                                USERTYPE.USERTYPE_INTERNSHIP_COORDINATOR_TRAINER,
                                USERTYPE.USERTYPE_SCHOOL_AND_COMPANY,
                            ],
                            ["schools.id"]: schoolId,
                        };

                        EntityApi.getCollectionCoordinator<Entity>(
                            1,
                            coordinatorParams
                        ).then(({ response }) => {
                            if (response !== null) {
                                setInternshipCoordinatorTrainer(
                                    response?.items
                                );
                            }
                        });
                    }
                    if (activeSchoolOrCompany && schoolId) {
                        const companyId =
                            activeSchoolOrCompany?.["@type"] ===
                            APPC.BE.Category.TYPE.TYPE_COMPANY
                                ? activeSchoolOrCompany?.["@id"]
                                : (internshipSchedule?.internship as Internship)
                                      ?.company?.["@id"];

                        const partnershipsStatusParams = {
                            pagination: false,
                            ["company.id"]: companyId,
                            ["school.id"]: schoolId,
                        };

                        PartnershipApi.getCollectionMyPartnershipsStatus<Partnership>(
                            1,
                            partnershipsStatusParams
                        ).then(({ errorMessage, response }) => {
                            if (errorMessage) {
                                toast({
                                    title: errorMessage,
                                    status: "error",
                                });
                            } else if (response !== null) {
                                if (response.totalItems > 0) {
                                    setInternshipReferenceTrainer(
                                        response?.items?.[0]
                                            ?.internshipReferenceTrainer as Entity
                                    );
                                }
                            }
                        });
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    if (loading) {
        <AppLoader />;
    }

    return (
        <AppFormContainer
            title={t("padm.InternshipScheduleStudentPage:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size="xl"
            isFooter={!isDetailPage}
        >
            <InternshipScheduleStudentDetailView
                data={data}
                internshipSchedule={internshipSchedule}
                internshipCoordinatorTrainer={internshipCoordinatorTrainer}
                internshipReferenceTrainer={internshipReferenceTrainer}
            />
        </AppFormContainer>
    );
};
